/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import type { MutableRefObject } from 'react';

import { Button, typography2 } from '@coursera/cds-core';
import { ChevronDownIcon } from '@coursera/cds-icons';
import { track } from '@coursera/event-pulse/sdk';

import _t from 'i18n!nls/megamenu';

type Props = {
  menuIsOpen: boolean;
  ref: MutableRefObject<HTMLButtonElement | null>;
  openMenuUsingKeyboard: () => void;
  openMenu: () => void;
  closeMenu: () => void;
};

const styles = {
  exploreButton: css`
    ${typography2.bodySecondary}
    color: var(--cds-color-neutral-primary-weak);

    :hover,
    :focus,
    :active {
      background-color: var(--cds-color-interactive-background-primary-hover-weak);
      text-decoration: none;
      color: var(--cds-color-interactive-primary-hover);

      & .cds-button-endIcon {
        color: var(--cds-color-neutral-primary-weak);
      }
    }
  `,
};

const ExploreButtonCDS = ({ menuIsOpen, ref, openMenuUsingKeyboard, openMenu, closeMenu }: Props) => {
  const toggleMenu = () => {
    if (!menuIsOpen) {
      track('open_navigation_menu', {
        navigationType: 'megamenu',
      });
      track('view_megamenu_items', {
        megamenuItemName: 'desktop_explore_button',
        megamenuSection: 'desktop_explore_button',
      });
      openMenuUsingKeyboard();
    } else {
      closeMenu();
    }
  };

  const onKeyUp = (evt: React.KeyboardEvent<unknown>) => {
    switch (evt.key) {
      case ' ':
        // because space does not trigger retracked click event like enter
        evt.preventDefault();
        toggleMenu();
        break;
      case 'Enter':
        evt.preventDefault();
        toggleMenu();
        break;
      default:
    }
  };

  const openMenuWithTracking = () => {
    openMenu();

    track('open_navigation_menu', {
      navigationType: 'megamenu',
    });
    track('view_megamenu_items', {
      megamenuItemName: 'desktop_explore_button',
      megamenuSection: 'desktop_explore_button',
    });
  };

  const closeMenuComponent = () => {
    closeMenu();
  };

  return (
    <Button
      data-testid="megamenu-explore-button"
      onMouseEnter={openMenuWithTracking}
      onMouseLeave={closeMenuComponent}
      onMouseDown={openMenuWithTracking}
      aria-expanded={menuIsOpen}
      aria-haspopup={true}
      aria-label={_t('Explore our catalog')}
      ref={ref}
      variant="ghost"
      icon={<ChevronDownIcon />}
      size="small"
      component="button"
      css={styles.exploreButton}
      onKeyUp={onKeyUp}
    >
      {_t('Explore')}
    </Button>
  );
};

export default ExploreButtonCDS;
